import React from "react";
import { useTranslation } from "react-i18next";
import { isArrayNullOrEmpty, toIndoFormat, REDIRECT_URL_ANDROID, EVENT_LINK_APP, sendTapEvent } from "../helper/helper";
import { PriceModel, QBPModel } from "../model/PriceModel";
import { colorGold, colorLight, colorPrimaryDark, colorYellowLight, colorYellowMedium } from "../rsc/colors";
import { ReactComponent as Mill } from "../rsc/mill.svg";
import { ReactComponent as PempemLogo } from "../rsc/pempem_logo.svg";
import { ReactComponent as Close } from "../rsc/close.svg";
import { ButtonPriceActionGold } from "./ButtonPriceActionGold";

type Props = {
    style?: React.CSSProperties
    price?: PriceModel
    close?: () => void
    onClick?: () => void
}

export const colorGreen300 = "#ABE220";
export const colorGreen500 = "#6FB62C";
export const colorApple050 = "#D2DA55";
export const colorGold600 = "#DABB55";
export const colorBrown200 = "#D2A371";

function backgroundColorQBP(position: number): string {
    switch (position) {
        case 0: return colorGreen500;
        case 1: return colorGreen300;
        case 2: return colorApple050;
        case 3: return colorGold600;
        case 4: return colorBrown200;
    }
    return colorLight;
}


export function CardPriceAtMill({ style, price, close, onClick }: Props) {
    const { t } = useTranslation();
    type QBPProps = {
        qbp: QBPModel
        index: number
    }

    const QBPRow = ({ qbp, index }: QBPProps) => {
        const priceToShow = qbp.price_pempem ? `${qbp.price_pempem}` : t('no_price_set');
        const color = backgroundColorQBP(index)
        const nameToShow = qbp.name ?? t('no_name');

        return <div className="pempem-flex-row gap10">
            <span className="bold pempem-flex-row-center padding5" style={{ backgroundColor: color, flex: 1 }}>{nameToShow}</span>
            {
                qbp.price_highlighted ?
                    <div className="pempem-flex-col-end" style={{width: 100}}>
                        <span className="text-tiny-plus bold">{toIndoFormat(parseInt(priceToShow))}</span>
                        <span className="text-tiny bold" style={{ color: '#1766A6' }}>Rp/Kg <span className="text-medium-plus">{toIndoFormat(parseInt(priceToShow))}</span></span>
                    </div>
                    :
                    <div style={{width: 100}}>
                        <span className="bold text-tiny bold">Rp/Kg <span style={{ fontSize: "20px" }}>{toIndoFormat(parseInt(priceToShow))}</span></span>
                    </div>
            }
        </div>
    }

    return (
        <div style={{ ...style, display: "flex", flexDirection: "column", background: colorYellowLight, border: `solid 3px ${colorGold} `, position: "relative" }}>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "5px", borderRadius: "5px", backgroundColor: colorYellowMedium, margin: "10px", padding: "5px 0" }}>
                <p style={{ textTransform: "uppercase", color: "#075255", fontWeight: 600, fontSize: "12px" }}>{t('price_at_mill_by')}</p>
                <PempemLogo style={{ height: "24px", width: "80px" }} fill={colorPrimaryDark} />
            </div>

            <div style={{ display: "flex", alignItems: "center", margin: "0 10px 0px 10px", gap: 15 }}>
                <Mill style={{ height: "32px", width: "32px", flexShrink: 0 }} />
                <div className="gap5" style={{ display: "flex", flexDirection: "column", flex: "1", margin: "0 0 0 10px" }}>
                    <p style={{ margin: 0, padding: 0, fontWeight: "600", fontSize: "15px" }}>{price?.millName}</p>
                    <span style={{ margin: 0, padding: 0, fontWeight: "300", fontSize: "13px" }}>{price?.location()}</span>
                </div>
                {
                    isArrayNullOrEmpty(price?.qbp_prices) &&
                    <div className="bold" style={{ display: "flex", alignItems: "baseline", gap: "5px" }}>
                        <p className="text-tiny-plus" style={{ margin: 0 }}>Rp/KG</p>
                        <p className="text-big" style={{ margin: 0 }}>{toIndoFormat(price?.pricePerUnit ?? 0)}</p>
                    </div>
                }
            </div>
            {(() => {
                const details = [
                    { label: t('minimum_quality'), value: price?.minimum_quality_level },
                    { label: t('require_spb'), value: price?.require_spb ? "✓" : null },
                    { label: t('grading_ratio'), value: price?.standard_grading_ratio ? `${price.standard_grading_ratio}%` : null },
                    { label: t('sorting'), value: price?.sorting_strictness?.toUpperCase() },
                    // { label: t('sorting'), value: (() => {
                    //     const level = price?.sorting_strictness;
                    //     if (level === 'High') return '🔴🔴🔴';
                    //     if (level === 'Medium') return '🟠🟠';
                    //     if (level === 'Low') return '🟢';
                    //     return null;
                    // })() },
                ].filter(d => d.value);
                return details.length > 0 ? (
                    <>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${details.length}, 1fr)`, gap: '10px', margin: '0 10px', padding: '10px' }}>
                        {details.map((detail, index) => (
                            <div key={index} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', fontWeight: '500', padding: '10px', background: 'linear-gradient(135deg, rgba(255,255,255,0.25), rgba(255,255,255,0.1))', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.5)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4), 0 2px 4px rgba(0,0,0,0.1)' }}>
                                <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '4px', color: '#075255', textTransform: 'uppercase' }}>{detail.label}</div>
                                <div style={{ color: '#333', filter: 'blur(5px)' , cursor: 'default' }}>
                                    {detail.value}
                                </div>
                            </div>
                        ))}
                    </div>
                    <a
                        href={REDIRECT_URL_ANDROID}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => sendTapEvent(EVENT_LINK_APP)}
                        style={{ display: 'block', margin: '0 10px 0px', fontSize: 13, color: '#075255', textAlign: 'center', fontStyle: 'italic', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                        🔓 {t('unlock_details_on_app')}
                    </a>
                    </>
                ) : null;
            })()}
            {
                !isArrayNullOrEmpty(price?.qbp_prices) &&
                <div className="pempem-flex-col" style={{ margin: "10px 10px 0 10px" }}>
                    {
                        price?.isProgramPrice() &&

                        <div className="pempem-flex-row-center gap10" style={{ margin: "10px 0px 10px 0px", textAlign: 'center' }}>
                            <span style={{ fontSize: 14 }} className="bold">
                                {t('in_partnership_with')}
                            </span>
                            <img
                                style={{ height: 25 }}
                                src={"assets/imgs/unilever_mini.svg"}
                                alt={`reward.unilever`} />
                        </div>
                    }
                    <div className="pempem-flex-col gap5">
                        {
                            price?.qbp_prices?.map((q, i) => <QBPRow qbp={q} index={i} />)
                        }
                    </div>
                </div>
            }
            <div className="pempem-flex-row-center" style={{ margin: '10px 10px 10px 10px' }}>
                <ButtonPriceActionGold style={{ width: '100%' }} onClick={() => onClick?.()} text={t('sell_to_pempem_at_mill')} />
            </div>
            {
                close &&
                <div onClick={() => close?.()} style={{ cursor: "pointer", position: "absolute", top: "-21px", right: "-13px", height: "42px", width: "42px", backgroundColor: colorGold, borderRadius: "21px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Close />
                </div>
            }
        </div >)
}